import { useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { DateRangePicker } from "@/components/views/DateRangePicker";
import { Package, Download, FileSpreadsheet, Loader2, ShieldCheck, Database, Info } from "lucide-react";
import { toast } from "sonner";
import { downloadFile } from "@/lib/api";
import { PermissionDenied, LoadingState } from "@/components/states";
import { usePermissions } from "@/hooks/usePermissions";

export default function DataPortability() {
  const perms = usePermissions();
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [expCfg, setExpCfg] = useState(false);
  const [expAct, setExpAct] = useState(false);

  if (perms.loading) return <LoadingState label="Loading…" />;
  const canConfig = perms.has("Export Configuration");
  const canActivity = perms.has("Export Activity");
  if (!canConfig && !canActivity) return <PermissionDenied message="You need an export permission to use Data & Portability." />;

  const exportConfig = async () => {
    setExpCfg(true);
    try { const name = await downloadFile("/export/configuration", { method: "POST" }); toast.success("Configuration exported", { description: name }); }
    catch (e) { toast.error(e.message); } finally { setExpCfg(false); }
  };
  const exportActivity = async () => {
    setExpAct(true);
    const qs = new URLSearchParams();
    if (start) qs.set("start", start);
    if (end) qs.set("end", end);
    try { const name = await downloadFile(`/export/activity.csv?${qs.toString()}`); toast.success("Activity exported", { description: name }); }
    catch (e) { toast.error(e.message); } finally { setExpAct(false); }
  };

  return (
    <div className="animate-fade-up" data-testid="data-portability">
      <PageHeader eyebrow="Settings" title="Data & Portability"
        subtitle="Keep portable, human-readable copies of your organisation configuration and activity history." />

      <Alert className="mb-6 border-accent/40 bg-accent-subtle" data-testid="backup-clarity">
        <Info className="h-4 w-4 text-accent" />
        <AlertTitle className="font-heading text-base">Configuration export is not a database backup</AlertTitle>
        <AlertDescription className="text-sm text-muted-foreground">
          Supabase PostgreSQL is the source of truth for your data and Supabase Storage for uploaded assets.
          These exports are for portability and human-readable backup of configuration — full database and storage
          backup / disaster recovery remain separate.
        </AlertDescription>
      </Alert>

      <div className="grid gap-5 lg:grid-cols-2">
        {canConfig && (
          <Card className="flex flex-col p-6 shadow-soft" data-testid="export-config-card">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary text-primary"><Package className="h-5 w-5" /></span>
            <h3 className="mt-3 font-heading text-lg font-semibold">Export configuration</h3>
            <p className="mt-1 flex-1 text-sm text-muted-foreground">
              A ZIP with versioned JSON (canonical) + CSV mirrors for tabular data + a manifest. Includes organisation
              settings, roles &amp; permissions, report recipients, navigation, themes, typography, layout, atmosphere,
              gradients, labels, notification policy and metadata references.
            </p>
            <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
              <ShieldCheck className="h-3.5 w-3.5 text-accent" /> Excludes all passwords, tokens, keys and font binaries.
            </p>
            <Button className="mt-4 gap-1.5" onClick={exportConfig} disabled={expCfg} data-testid="export-config-btn">
              {expCfg ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Export configuration (ZIP)
            </Button>
          </Card>
        )}

        {canActivity && (
          <Card className="flex flex-col p-6 shadow-soft" data-testid="export-activity-card">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary text-primary"><FileSpreadsheet className="h-5 w-5" /></span>
            <h3 className="mt-3 font-heading text-lg font-semibold">Export activity (CSV)</h3>
            <p className="mt-1 flex-1 text-sm text-muted-foreground">
              Download the audit/activity history as a UTF-8 CSV. Choose a date range (or leave blank for all).
              Full search &amp; column filters are available on the Activity page’s Export button too.
            </p>
            <div className="mt-3">
              <label className="mb-1 block text-xs text-muted-foreground">Date range</label>
              <DateRangePicker start={start} end={end} onChange={(s, e) => { setStart(s); setEnd(e); }} testid="portability-range" />
            </div>
            <Button variant="outline" className="mt-4 gap-1.5" onClick={exportActivity} disabled={expAct} data-testid="export-activity-btn">
              {expAct ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Export activity CSV
            </Button>
          </Card>
        )}
      </div>

      <p className="mt-6 flex items-center gap-1.5 text-xs text-muted-foreground">
        <Database className="h-3.5 w-3.5" /> Exports are architected so large future activity histories can move to asynchronous generation without changing this page.
      </p>
    </div>
  );
}
